import { Layout, Menu } from "antd";
import * as React from "react";
import {
    Link,
    withRouter,
    Route,
    RouteComponentProps,
    Switch,
} from "react-router-dom";
import { BuxTooltip } from "../../Common/";

import { hasFeature, enableTimeoutFeature } from "../../../Actions/UserActions";
import { getTranslation } from "../../../Actions/VerbiageActions";
import { IUser } from "../../../Models/IUser";
import {
    IBuxtonSupportTeamMember,
    IStore,
    ITerritory,
    ITerritoryType,
    ITerritoryTypePermission,
    IUserPreferences,
    IUserProfile,
} from "../../../Models/Models";
import TerritoryManagement from "./Components/TerritoryManagement";
import UserManagement from "./Components/UserManagement";
import UserPreferences from "./Components/UserPreferences";
import UserProfile from "./Components/UserProfile";
import "./UserSettings.css";
import { UserSettingsMenuSelection, OktaGroups } from "../../../Enums/Enums";
import AccountTeam from "./Components/AccountTeam";
import { IOktaMFAFactor } from "../../../Models/OktaModels/IOktaMFAFactor";
import { ScoutFeature } from "../../../Enums/ScoutFeature";
import { AppArea } from "../../../Enums/AppArea";
import { IUserShareSettings } from "../../../Models/IUserShareSettings";
import {TimeoutManagement} from "./Components/TimeoutManagement";

const SubMenu = Menu.SubMenu;

interface IUserSettingsProps extends RouteComponentProps {
  user: IUser;
  userShareSettings: IUserShareSettings;
  allUsers: Array<IUser>;
  userPreferences: IUserPreferences;
  userFranchiseeStores: Array<IStore>;
  userFranchiseeTerritories: Array<ITerritory>;
  territoryTypePermissions: Array<ITerritoryTypePermission>;
  companySupportTeam: Array<IBuxtonSupportTeamMember>;
  communitySites: Array<IStore>;
  allMFAFactors: IOktaMFAFactor[];
  selectedMFAFactorID: string;
  waitingForMFAResponse: boolean;
  mfaResult: string;
  showMfaEnrollmentModal: boolean;
  mfaEnrollmentModalText: string;
  mfaEnrollmentUpdatedThisSession: boolean;
  saveProfileLoading: boolean;
  getUserPreferences: () => void;
  updateUserPreferences: (userPreferences: IUserPreferences) => void;
  getUserFranchiseeStoreInformation: (userID: number) => void;
  getUserFranchiseeTerritoryInformation: (userID: number) => void;
  removeStoreFromFranchiseeUser: (userID: number, storeID: number) => void;
  removeTerritoryFromFranchiseeUser: (
    userID: number,
    territoryID: number
  ) => void;
  updateUserActiveStatus: (userID: number, activeStatus: boolean) => void;
  updateTerritoryTypeAdminOnlyProperty: (territoryType: ITerritoryType) => void;
  updateTerritoryTypeUserPermissions: (
    userID: number,
    territoryID: number,
    canView: boolean,
    canEdit: boolean
  ) => void;
  getTerritoryTypePermissions: (territoryTypeID: number) => void;
  updateUserProfile: (
    profileUpdateObj: IUserProfile,
    shareSettingsUpdateObj: IUserShareSettings
  ) => void;
  updateUserAvatar: (avatarImg: File) => void;
  deleteUserAvatar: (avatarStr: string) => void;
  updateShareImage: (shareImg: File) => void;
  deleteShareImage: (imgStr: string) => void;
  resetPassword: () => void;
  getCompanySupportTeam: () => void;
  getAllCommunitySites: () => void;
  updateTerritoryTypeUserPermissionsForAll: (
    territoryPermissions: Array<ITerritoryTypePermission>
  ) => void;
  getCompanyTerritoryTypes: () => void;
  GetMFAFactors: (oktaUserID: string) => void;
  IssueMFAChallenge: (factor: IOktaMFAFactor) => void;
  IssuePushMFAChallenge: (factor: IOktaMFAFactor) => void;
  setAllMFAFactors: (factors: IOktaMFAFactor[]) => void;
  setSelectedFactorID: (id: string) => void;
  setWaitingForMFAResponse: (waiting: boolean) => void;
  SubmitMFAChallengeResponse: (
    factor: IOktaMFAFactor,
    response: string
  ) => void;
  PollForPushResponse: (factor: IOktaMFAFactor) => void;
  setShowMfaEnrollmentModal: (show: boolean) => void;
  setMfaEnrollmentUpdatedThisSession: (updated: boolean) => void;
  updateSessionTimeoutLength: (cid: number, newTimeoutLength: number) => void;
}

interface IUserSettingsState {
  selectedMenuItem: UserSettingsMenuSelection;
}

class UserSettings extends React.Component<
  IUserSettingsProps,
  IUserSettingsState
> {
  private defaultOpenKeys: Array<string> = [
      UserSettingsMenuSelection.ACCOUNT_INFORMATION.toString(),
      UserSettingsMenuSelection.COMPANY_MANAGEMENT.toString(),
  ];

  constructor(props: IUserSettingsProps) {
      super(props);

      const defaultSelectedMenuItem
      = this.props.user.oktaGroups.indexOf(OktaGroups.BUXTONCORP)
        === -1
      || this.props.user.oktaGroups.indexOf(
          OktaGroups.MULTIACCOUNTUSERS
      ) === -1
          ? UserSettingsMenuSelection.USER_PREFERENCES
          : UserSettingsMenuSelection.USER_PROFILE;

      this.state = {
          selectedMenuItem: defaultSelectedMenuItem,
      };
  }

  public render() {
      const accountInformationComponent: JSX.Element = (
          <SubMenu
              key={UserSettingsMenuSelection.ACCOUNT_INFORMATION}
              title="Account Information"
              className="react-user-settings-account-information-menu-title"
          >
              {this.props.user.oktaGroups.indexOf(
                  OktaGroups.BUXTONCORP
              ) === -1
          && this.props.user.oktaGroups.indexOf(
              OktaGroups.MULTIACCOUNTUSERS
          ) === -1 && (
                  <Menu.Item
                      key={UserSettingsMenuSelection.USER_PROFILE}
                      onClick={this.setSelectedMenuItem}
                      className="react-user-settings-account-information-menu-item"
                      style={{ position: "relative" }}
                  >
                      <Link to="Profile">User Profile</Link>
                      <BuxTooltip
                          placement="right"
                          content="Edit your personal details such as name, email, password, and even upload a profile picture."
                          trigger="click"
                          allowClickThrough={false}
                          style={{
                              position: "absolute",
                              right: 12,
                              top: 0,
                              marginTop: "12px",
                          }}
                      />
                  </Menu.Item>
              )}
              <Menu.Item
                  key={UserSettingsMenuSelection.USER_PREFERENCES}
                  onClick={this.setSelectedMenuItem}
                  className="react-user-settings-account-information-menu-item"
                  style={{ position: "relative" }}
              >
                  <Link to="Preferences">Preferences</Link>
                  <BuxTooltip
                      placement="right"
                      content="Set defaults and customize your SCOUT experience."
                      trigger="click"
                      allowClickThrough={false}
                      style={{
                          position: "absolute",
                          right: 12,
                          top: 0,
                          marginTop: "12px",
                      }}
                  />
              </Menu.Item>
              <Menu.Item
                  key={UserSettingsMenuSelection.ACCOUNT_TEAM}
                  onClick={this.setSelectedMenuItem}
                  className="react-user-settings-account-information-menu-item"
                  style={{ position: "relative" }}
              >
                  <Link to="AccountTeam">Account Team</Link>
                  <BuxTooltip
                      placement="right"
                      content="Provides details on your Account Management and Support Team."
                      trigger="click"
                      allowClickThrough={false}
                      style={{
                          position: "absolute",
                          right: 12,
                          top: 0,
                          marginTop: "12px",
                      }}
                  />
              </Menu.Item>
          </SubMenu>
      );

      let userManagementSubMenuComponent: JSX.Element = null;
      const territoryWord = getTranslation(
          this.props.user.company.companyType,
          AppArea.TERRITORIES,
          true
      );

      if (
          this.props.user.isAdmin && hasFeature(this.props.user, ScoutFeature.AdminTools)
      ) {
          userManagementSubMenuComponent = (
              <SubMenu
                  key={UserSettingsMenuSelection.COMPANY_MANAGEMENT}
                  title="Management"
                  className="react-user-settings-account-information-menu-title"
              >
                  <Menu.Item
                      key={UserSettingsMenuSelection.USER_MANAGEMENT}
                      onClick={this.setSelectedMenuItem}
                      className="react-user-settings-account-information-menu-item"
                  >
                      <Link to="ManageUsers">Users</Link>
                  </Menu.Item>
                  {hasFeature(this.props.user, ScoutFeature.Scout4)
                              && <Menu.Item
                                  key={UserSettingsMenuSelection.TERRITORY_MANAGEMENT}
                                  onClick={this.setSelectedMenuItem}
                                  className="react-user-settings-account-information-menu-item"
                              >
                                  <Link to="ManageTerritories">{territoryWord}</Link>
                              </Menu.Item>}
                  { hasFeature(this.props.user, ScoutFeature.InactivityTimeout) &&
                      <Menu.Item
                          key={UserSettingsMenuSelection.TIMEOUT_MANAGEMENT}
                          onClick={this.setSelectedMenuItem}
                          className="react-user-settings-account-information-menu-item"
                      >
                          <Link to="ManageTimeout">User Timeout</Link>
                      </Menu.Item> }
              </SubMenu>
          );
      }

      return (
          <Layout>
              <Layout.Content>
                  <div className="UserSettings container">
                      <Layout.Sider
                          width={256}
                          theme="light"
                          style={{
                              background: "#fff",
                              border: "1px solid rgb(230, 230, 230)",
                          }}
                      >
                          <Menu
                              mode="inline"
                              style={{ paddingTop: "16px", borderRight: "none" }}
                              defaultSelectedKeys={this.getDefaultSelectedKey()}
                              defaultOpenKeys={this.defaultOpenKeys}
                          >
                              {accountInformationComponent}
                              {userManagementSubMenuComponent}
                          </Menu>
                      </Layout.Sider>
                      <Layout.Content>
                          <Switch>
                              {this.props.user.oktaGroups.indexOf(
                                  OktaGroups.BUXTONCORP
                              ) === -1
                  && this.props.user.oktaGroups.indexOf(
                      OktaGroups.MULTIACCOUNTUSERS
                  ) === -1 && (
                                  <Route
                                      exact={true}
                                      path={`${this.props.match.path}/Profile`}
                                      render={() => (
                                          <UserProfile
                                              user={this.props.user}
                                              userShareSettings={this.props.userShareSettings}
                                              updateUserProfile={this.props.updateUserProfile}
                                              resetPassword={this.props.resetPassword}
                                              updateUserAvatar={this.props.updateUserAvatar}
                                              deleteUserAvatar={this.props.deleteUserAvatar}
                                              updateShareImage={this.props.updateShareImage}
                                              deleteShareImage={this.props.deleteShareImage}
                                              allMFAFactors={this.props.allMFAFactors}
                                              GetMFAFactors={this.props.GetMFAFactors}
                                              IssueMFAChallenge={this.props.IssueMFAChallenge}
                                              IssuePushMFAChallenge={
                                                  this.props.IssuePushMFAChallenge
                                              }
                                              selectedMFAFactorID={this.props.selectedMFAFactorID}
                                              waitingForMFAResponse={
                                                  this.props.waitingForMFAResponse
                                              }
                                              mfaResult={this.props.mfaResult}
                                              setAllMFAFactors={this.props.setAllMFAFactors}
                                              setSelectedFactorID={this.props.setSelectedFactorID}
                                              setWaitingForMFAResponse={
                                                  this.props.setWaitingForMFAResponse
                                              }
                                              SubmitMFAChallengeResponse={
                                                  this.props.SubmitMFAChallengeResponse
                                              }
                                              PollForPushResponse={this.props.PollForPushResponse}
                                              showMfaEnrollmentModal={
                                                  this.props.showMfaEnrollmentModal
                                              }
                                              mfaEnrollmentModalText={
                                                  this.props.mfaEnrollmentModalText
                                              }
                                              setShowMfaEnrollmentModal={
                                                  this.props.setShowMfaEnrollmentModal
                                              }
                                              mfaEnrollmentUpdatedThisSession={
                                                  this.props.mfaEnrollmentUpdatedThisSession
                                              }
                                              setMfaEnrollmentUpdatedThisSession={
                                                  this.props.setMfaEnrollmentUpdatedThisSession
                                              }
                                              saveProfileLoading={this.props.saveProfileLoading}
                                          />
                                      )}
                                  />
                              )}
                              <Route
                                  exact={true}
                                  path={`${this.props.match.path}/Preferences`}
                                  render={() => (
                                      <UserPreferences
                                          user={this.props.user}
                                          userPreferences={this.props.userPreferences}
                                          communitySites={this.props.communitySites}
                                          updateUserPreferences={this.props.updateUserPreferences}
                                          getUserPreferences={this.props.getUserPreferences}
                                          getAllCommunitySites={this.props.getAllCommunitySites}
                                      />
                                  )}
                              />
                              <Route
                                  exact={true}
                                  path={`${this.props.match.path}/AccountTeam`}
                                  render={() => (
                                      <AccountTeam
                                          user={this.props.user}
                                          companySupportTeam={this.props.companySupportTeam}
                                          getCompanySupportTeam={this.props.getCompanySupportTeam}
                                      />
                                  )}
                              />
                              {
                                  this.props.user.isAdmin && hasFeature(this.props.user, ScoutFeature.AdminTools)
                              && <Route
                                  exact={true}
                                  path={`${this.props.match.path}/ManageUsers`}
                                  render={() => (
                                      <UserManagement
                                          user={this.props.user}
                                          allUsers={this.props.allUsers}
                                          userFranchiseeStores={this.props.userFranchiseeStores}
                                          userFranchiseeTerritories={
                                              this.props.userFranchiseeTerritories
                                          }
                                          getUserFranchiseeTerritoryInformation={
                                              this.props.getUserFranchiseeTerritoryInformation
                                          }
                                          getUserFranchiseeStoreInformation={
                                              this.props.getUserFranchiseeStoreInformation
                                          }
                                          removeStoreFromFranchiseeUser={
                                              this.props.removeStoreFromFranchiseeUser
                                          }
                                          removeTerritoryFromFranchiseeUser={
                                              this.props.removeTerritoryFromFranchiseeUser
                                          }
                                          updateUserActiveStatus={this.props.updateUserActiveStatus}
                                      />
                                  )}
                              />
                              }
                              {
                                  (this.props.user.isAdmin && hasFeature(this.props.user, ScoutFeature.AdminTools)) && hasFeature(this.props.user, ScoutFeature.Scout4)
                              && <Route
                                  exact={true}
                                  path={`${this.props.match.path}/ManageTerritories`}
                                  render={() => (
                                      <TerritoryManagement
                                          user={this.props.user}
                                          territoryTypePermissions={
                                              this.props.territoryTypePermissions
                                          }
                                          //companyTerritoryTypes={this.props.companyTerritoryTypes}

                                          getTerritoryTypePermissions={
                                              this.props.getTerritoryTypePermissions
                                          }
                                          updateTerritoryTypeAdminOnlyProperty={
                                              this.props.updateTerritoryTypeAdminOnlyProperty
                                          }
                                          updateTerritoryTypeUserPermissions={
                                              this.props.updateTerritoryTypeUserPermissions
                                          }
                                          updateTerritoryTypeUserPermissionsForAll={
                                              this.props.updateTerritoryTypeUserPermissionsForAll
                                          }
                                          getCompanyTerritoryTypes={
                                              this.props.getCompanyTerritoryTypes
                                          }
                                      ></TerritoryManagement>
                                  )}
                              />
                              }
                              {
                                  (this.props.user.isAdmin && hasFeature(this.props.user, ScoutFeature.AdminTools)) && hasFeature(this.props.user, ScoutFeature.InactivityTimeout)
                                  && <Route
                                      exact={true}
                                      path={`${this.props.match.path}/ManageTimeout`}
                                      render={() => (
                                          <TimeoutManagement
                                            userPreferences={this.props.userPreferences}
                                            user={this.props.user}
                                            getUserPreferences={this.props.getUserPreferences}
                                            updateSessionTimeoutLength={this.props.updateSessionTimeoutLength}/>
                                      )}
                                  />
                              }
                          </Switch>
                      </Layout.Content>
                  </div>
              </Layout.Content>
          </Layout>
      );
  }

  private setSelectedMenuItem = (e): void => {
      const realKey: number = parseInt(e.key, 10); // parameters (thingToParse, base number)

      if (this.state.selectedMenuItem !== realKey) {
      // they clicked on a different section

          this.setState({
              selectedMenuItem: realKey,
          });
      }
  };

  private getDefaultSelectedKey = (): Array<string> => {
      if (this.props.location.pathname.indexOf("ManageUsers") > -1) {
          return [UserSettingsMenuSelection.USER_MANAGEMENT.toString()];
      } else if (this.props.location.pathname.indexOf("ManageTerritories") > -1) {
          return [UserSettingsMenuSelection.TERRITORY_MANAGEMENT.toString()];
      } else if (this.props.location.pathname.indexOf("AccountTeam") > -1) {
          return [UserSettingsMenuSelection.ACCOUNT_TEAM.toString()];
      } else if (this.props.location.pathname.indexOf("Preferences") > -1) {
          return [UserSettingsMenuSelection.USER_PREFERENCES.toString()];
      } else {
          return [UserSettingsMenuSelection.USER_PROFILE.toString()];
      }
  };
}

export default withRouter(UserSettings);
